import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Search, UserPlus, X, Loader2 } from "lucide-react";

/**
 * Ricerca/autocomplete cliente efficiente (nome, cognome, telefono, email).
 * Non carica l'intera lista clienti: interroga il backend /clients?q= mentre si digita.
 * Consente la creazione al volo di una nuova cliente senza perdere il contesto.
 *
 * Props:
 *  - value: client object { id, first_name, last_name, phone, email } | null
 *  - onSelect(client): callback con il cliente selezionato/creato
 */
export function ClientSearch({ value, onSelect }) {
  const [editing, setEditing] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newForm, setNewForm] = useState({ first_name: "", last_name: "", phone: "", email: "" });
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef(null);

  // Ricerca debounced (250ms). A query vuota non mostra nulla (niente lista completa).
  useEffect(() => {
    if (!editing && value) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const term = query.trim();
    if (term.length < 1) { setResults([]); setLoading(false); return; }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/clients?q=${encodeURIComponent(term)}&limit=8`);
        setResults(data || []);
      } catch (e) {
        setResults([]);
      } finally { setLoading(false); }
    }, 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, editing, value]);

  const pick = (c) => {
    onSelect(c);
    setEditing(false);
    setQuery("");
    setResults([]);
    setCreating(false);
  };

  const startCreate = () => {
    // Precompila il nome con quanto digitato (comodità).
    const parts = query.trim().split(" ");
    setNewForm({ first_name: parts[0] || "", last_name: parts.slice(1).join(" ") || "", phone: "", email: "" });
    setCreating(true);
  };

  const saveNew = async () => {
    if (!newForm.first_name.trim()) { toast.error("Il nome è obbligatorio"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/clients", newForm);
      toast.success("Cliente creata");
      pick(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore creazione cliente");
    } finally { setSaving(false); }
  };

  // Vista compatta: cliente già selezionata
  if (value && !editing) {
    return (
      <div className="flex items-center justify-between rounded-md border border-border p-2.5" data-testid="client-search-selected">
        <div className="min-w-0">
          <div className="font-medium truncate">{value.first_name} {value.last_name}</div>
          {(value.phone || value.email) && (
            <div className="text-xs text-muted-foreground truncate">{[value.phone, value.email].filter(Boolean).join(" · ")}</div>
          )}
        </div>
        <Button type="button" variant="ghost" size="sm" className="shrink-0" onClick={() => { setEditing(true); setQuery(""); }} data-testid="client-search-change">Cambia</Button>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          value={query}
          onChange={(e) => { setQuery(e.target.value); setCreating(false); }}
          placeholder="Cerca per nome, cognome, telefono o email..."
          className="pl-8"
          autoComplete="off"
          data-testid="client-search-input"
        />
        {(value && editing) && (
          <button type="button" onClick={() => { setEditing(false); setQuery(""); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="client-search-cancel"><X className="h-4 w-4" /></button>
        )}
      </div>

      {/* Form nuova cliente inline */}
      {creating ? (
        <div className="mt-2 rounded-md border border-border p-3 space-y-2" data-testid="client-new-form">
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1"><Label className="text-xs">Nome *</Label><Input value={newForm.first_name} onChange={(e) => setNewForm({ ...newForm, first_name: e.target.value })} data-testid="client-new-first-name" /></div>
            <div className="space-y-1"><Label className="text-xs">Cognome</Label><Input value={newForm.last_name} onChange={(e) => setNewForm({ ...newForm, last_name: e.target.value })} data-testid="client-new-last-name" /></div>
            <div className="space-y-1"><Label className="text-xs">Telefono</Label><Input value={newForm.phone} onChange={(e) => setNewForm({ ...newForm, phone: e.target.value })} data-testid="client-new-phone" /></div>
            <div className="space-y-1"><Label className="text-xs">Email</Label><Input value={newForm.email} onChange={(e) => setNewForm({ ...newForm, email: e.target.value })} data-testid="client-new-email" /></div>
          </div>
          <div className="flex gap-2">
            <Button type="button" size="sm" className="flex-1" onClick={saveNew} disabled={saving} data-testid="client-new-save">{saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Crea e seleziona</Button>
            <Button type="button" size="sm" variant="outline" onClick={() => setCreating(false)}>Annulla</Button>
          </div>
        </div>
      ) : (
        <div className="mt-1 rounded-md border border-border overflow-hidden">
          {loading && (
            <div className="flex items-center gap-2 px-3 py-2.5 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Ricerca...</div>
          )}
          {!loading && query.trim().length > 0 && results.length === 0 && (
            <div className="px-3 py-2.5 text-sm text-muted-foreground">Nessuna cliente trovata</div>
          )}
          {!loading && results.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => pick(c)}
              className="w-full text-left px-3 py-2 hover:bg-accent flex items-center justify-between border-b border-border last:border-b-0"
              data-testid={`client-search-result-${c.id}`}
            >
              <span className="min-w-0">
                <span className="font-medium block truncate">{c.first_name} {c.last_name}</span>
                {(c.phone || c.email) && <span className="text-xs text-muted-foreground block truncate">{[c.phone, c.email].filter(Boolean).join(" · ")}</span>}
              </span>
            </button>
          ))}
          {query.trim().length === 0 && !loading && (
            <div className="px-3 py-2.5 text-xs text-muted-foreground">Digita per cercare una cliente</div>
          )}
          <button type="button" onClick={startCreate} className="w-full text-left px-3 py-2.5 hover:bg-accent flex items-center gap-2 text-[hsl(var(--hi-gold))] font-medium border-t border-border" data-testid="client-search-add-new">
            <UserPlus className="h-4 w-4" /> Nuova cliente{query.trim() ? ` "${query.trim()}"` : ""}
          </button>
        </div>
      )}
    </div>
  );
}
